import { html, css, nothing, LitElement } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import { navigateBack } from '../../store/route/route.js';
import { previewElearning } from '../../store/preview/preview.js';
import { translate } from '../../helpers/translate.js';
import { formatDateTime } from '../../helpers/controls.js';
import '@sdb-develop/learning-frontend';
import '@polymer/paper-input/paper-input.js';
import '@polymer/iron-form/iron-form.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-overlay-layout.js';

@customElement('sdb-os-item')
class SdbOsItem extends connect(store)(LitElement) {

    @property({ type: String })
    accessor targetId;

    @state()
    accessor osItem;

    @state()
    accessor isLoading = false;

    @state()
    accessor isDirty = false;

    @query('#os_item_form')
    accessor osItemForm;

    get latestVersion() {
        if (!this.osItem.versions?.length)
            return null;
        return this.osItem.versions[this.osItem.versions.length-1];
    }

    get versionLogs() {
        return this.osItem.log.filter(log =>  typeof log.version === "number");
    }

    get latestVersionLog() {
        return this.osItem.log?.find(log => log.version === this.osItem.latestVersion);
    }

    stateChanged(state) {
        this.osItem = state.os?.current;
        this.isLoading = state.os?.status === 'loading' || !state.os?.current;
        this.isDirty = state.os?.dirty;
        this.settings = state.env.settings;
        this.user = state.auth.user;
        this.portalId = state.env.portalIdentifier;
    }

    static get styles() {
        return [
            sharedStyles,
            css`
                [slot=content] {
                    width: 100%;
                    padding: 0 20px;
                    max-width: 1400px;
                }

                sdb-content-card {
                    max-width: 1400px;
                    margin-bottom: 16px;
                }

                sdb-content-card .headline {
                    display: inline-flex;
                    align-items: center;
                    color: var(--text-primary);
                    padding: 10px 0 20px 0;
                }

                sdb-content-card .headline span {
                    margin-left: 24px;
                    font-size: 16px;
                    font-family: var(--font-family);
                }

                paper-input {
                    margin-bottom: 5px;
                }

                paper-input[required] label {
                    color: red;
                }

                vaadin-select {
                    width: 30%;
                    min-width: 200px;
                }

                .uploader_container {
                    margin-top: 30px;
                }

                .uploader_container > span {
                    display: block;
                    font-size: 14px;
                    margin-bottom: 10px;
                    margin-left: 25px;
                }
            `,
        ];
    }

    render() {
        if (this.isLoading || this.targetId !== this.osItem.targetId) {
            return html`<sdb-loading></sdb-loading>`;
        }

        return html`
            <sdb-overlay-layout
                headline=${this.osItem.title}
                ?dirty=${this.isDirty}
                @back=${() => { store.dispatch(navigateBack('os')); }}>

                <div slot="buttons">
                    <sdb-content-button
                        id="preview_button"
                        label=${translate('preview')}
                        primary
                        ?disabled=${this.isDirty}
                        @click=${()=> { store.dispatch(previewElearning({ item: this.osItem })); }}>
                    </sdb-content-button>
                </div>

                <iron-form id="os_item_form" slot="content">
                    <form>
                        <sdb-layout-100>

                            <sdb-content-card id="general_card">
                                <paper-input
                                    id="name_input"
                                    transparent
                                    label=${translate('name')}
                                    value=${this.osItem.title}
                                    readonly>
                                </paper-input>

                                <paper-input
                                    id="supplier_input"
                                    transparent
                                    label=${translate('supplier')}
                                    value=${this.osItem.supplier.name}
                                    readonly>
                                </paper-input>
                            </sdb-content-card>

                            <sdb-content-card id="versions_card">
                                <div class="headline">
                                    <sdb-content-icon
                                        icon="fal fa-file-zip"
                                        size="32">
                                    </sdb-content-icon>
                                    <span>${translate('versions')}<span>
                                </div>

                                <sdb-content-accordion>
                                    <sdb-content-accordionitem
                                        overline=${translate('latestVersion')}
                                        headline=${this.latestVersion.title}
                                        subtitle=${formatDateTime(this.latestVersionLog?.timestamp)}>
                                        <sdb-content-list class="scroller">
                                            <sdb-content-listheading>
                                                <sdb-content-headingitem
                                                    textDark
                                                    slot="1"
                                                    headline=${translate('version')}>
                                                </sdb-content-headingitem>
                                                <sdb-content-headingitem
                                                    textDark
                                                    slot="2"
                                                    headline=${translate('status')}>
                                                </sdb-content-headingitem>
                                                <sdb-content-headingitem
                                                    textDark
                                                    slot="3"
                                                    headline=${translate('published')}>
                                                </sdb-content-headingitem>
                                                <sdb-content-headingitem
                                                    textDark
                                                    slot="4"
                                                    headline=${translate('publishedBy')}>
                                                </sdb-content-headingitem>
                                            </sdb-content-listheading>
                                            ${map([...this.versionLogs].reverse(), (version) => html`
                                                <sdb-content-listitem
                                                    headline=${version.version}
                                                    noPadding
                                                    noHover>
                                                    <span slot="2">${this._getStatus(version)}</span>
                                                    <span slot="3">${formatDateTime(version.timestamp)}</span>
                                                    <span slot="4">${version.displayName}</span>
                                                </sdb-content-listitem>
                                            `)}
                                        </sdb-content-list>
                                    </sdb-content-accordionitem>
                                </sdb-content-accordion>

                            </sdb-content-card>

                        </sdb-layout-100>
                    </form>
                </iron-form>
            </sdb-overlay-layout>
        `;
    }

    firstUpdated() {
        this.dispatchEvent(
            new CustomEvent('fullscreen', {
                bubbles: true,
                composed: true,
                detail: {
                    fullscreen: true,
                },
            }),
        );
    }

    _getStatus(version) {
        if (version.version === this.osItem.latestVersion) // check if latest
            return translate('latestVersion');
        if (version.version === this.osItem.versions.length) // if not than last is preview
            return translate('previewVersion');
        return '';
    }
}
